/**
 * Containers: cards, page headers, section rules and stat tiles.
 * These set the page rhythm, so they are deliberately opinionated about
 * spacing and never take arbitrary padding.
 */
import type { CSSProperties, ReactNode } from 'react';
import { cx } from '@/lib/cx';

export function Card({ children, className, pad = false, interactive, selected, style, ...rest }: {
  children: ReactNode;
  className?: string;
  /** Applies the standard internal padding. Omit when the card holds a table. */
  pad?: boolean;
  /** Hover-lifts — for cards that are links or selectable tiles. */
  interactive?: boolean;
  selected?: boolean;
  style?: CSSProperties;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className' | 'style'>) {
  return (
    <div
      className={cx('card', pad && 'card-pad', interactive && 'interactive', selected && 'selected', className)}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHead({ title, subtitle, children }: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned actions. */
  children?: ReactNode;
}) {
  return (
    <div className="card-head">
      <div className="stack grow">
        <span className="card-title">{title}</span>
        {subtitle && <span className="card-sub">{subtitle}</span>}
      </div>
      {children && <span className="row gap-2 none">{children}</span>}
    </div>
  );
}

export function CardFoot({ children }: { children: ReactNode }) {
  return <div className="card-foot">{children}</div>;
}

export function PageHeader({ title, description, eyebrow, actions }: {
  title: string;
  description?: ReactNode;
  /** Small uppercase context line above the title (e.g. the tool family). */
  eyebrow?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="grow">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1 className="page-title">{title}</h1>
        {description && <p className="page-desc">{description}</p>}
      </div>
      {actions && <div className="row gap-2 wrap none">{actions}</div>}
    </header>
  );
}

/** The rule between sections of a page: heading left, actions right. */
export function SectionHead({ title, children, id }: { title: ReactNode; children?: ReactNode; id?: string }) {
  return (
    <div className="section-head">
      <h2 className="section-title" id={id}>{title}</h2>
      {children && <div className="row gap-2">{children}</div>}
    </div>
  );
}

/** A headline number. `to` turns the whole tile into a link. */
export function Stat({ n, k, hint, tone }: {
  n: ReactNode;
  k: string;
  hint?: ReactNode;
  tone?: 'ok' | 'warn' | 'danger';
}) {
  return (
    <Card className="stat">
      <div className={cx('n', 'mono', tone && `text-${tone}`)}>{n}</div>
      <div className="k">{k}</div>
      {hint && <div className="t-xs muted">{hint}</div>}
    </Card>
  );
}
